// CreateShipmentModal.tsx
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Block,
  Button,
  FlexContainer,
  H4,
  InputCheckbox,
  InputSelect,
  InputText,
  LanguageTypes,
  Modal,
  SoundNotificationContext,
  useNotificationsV2,
} from '@uni-space/uni-components';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createShipment, getDropOffPoints } from '../api';
import { AxiosError } from 'axios';
import { ErrorDTO } from '@shared/types/api';
import { initCreateShipmentData, XDocCreateShipmentInputId, XDocCreateShipmentMap } from '../model/CreateShipmentState';

export default function CreateShipmentModal({
  onRefetch,
  onClose,
  isOpen,
}: {
  onRefetch?: () => void;
  onClose: () => void;
  isOpen: boolean;
}) {
  const { t, i18n } = useTranslation();
  const { createNotification } = useNotificationsV2();
  const { onSoundSuccess, onSoundError } = useContext(SoundNotificationContext);
  const [shipmentState, setShipmentState] = useState<XDocCreateShipmentMap>(initCreateShipmentData);

  const handleChangeShipmentState = <T extends keyof XDocCreateShipmentMap>(
    key: T,
    value: XDocCreateShipmentMap[T],
  ) => {
    setShipmentState((prev) => ({ ...prev, [key]: value }));
  };

  // Смена флага меняет список точек — ранее выбранная точка становится невалидной
  const handleChangeIsReturn = (checked: boolean) => {
    setShipmentState((prev) => ({ ...prev, isReturn: checked, nextDropOffPointId: '' }));
  };

  useEffect(() => {
    if (!isOpen) {
      setShipmentState(initCreateShipmentData);
    }
  }, [isOpen]);

  const {
    data: dropOffPoints,
    // isLoading, а не isFetching: при повторном переключении флага список берётся из кэша,
    // и фоновое обновление не должно мигать скелетоном
    isLoading: isDropOffPointsLoading,
    isError: isDropOffPointsError,
  } = useQuery({
    queryKey: ['getDropOffPoints', shipmentState.isReturn],
    queryFn: ({ signal }) => getDropOffPoints(shipmentState.isReturn, { signal }),
    select: (res) => res.items.map((point) => ({ id: point.id, label: point.name })),
    enabled: isOpen,
  });

  const selectedDropOffPointLabel =
    dropOffPoints?.find((point) => point.id === shipmentState.nextDropOffPointId)?.label ?? '';

  const { mutateAsync: onCreateShipment, isPending } = useMutation({
    mutationKey: ['createAcceptance'],
    mutationFn: createShipment,
    onSuccess: () => {
      onSoundSuccess();
      createNotification({
        type: 'success',
        message: {
          title: t('shipmentCreate.notify.success.title'),
          desc: t('shipmentCreate.notify.success.desc'),
        },
      });
      onClose();
      onRefetch?.();
    },
    onError: (e: AxiosError<ErrorDTO>) => {
      onSoundError();
      createNotification({
        type: 'error',
        message: { title: t('shipmentCreate.notify.error.title'), desc: e.response?.data.message },
      });
    },
  });

  const handleSubmit = () => {
    const data = {
      vehicleNumber: shipmentState.vehicleNumber,
      isReturn: shipmentState.isReturn,
      nextDropOffPointId: shipmentState.nextDropOffPointId,
    };
    onCreateShipment(data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <FlexContainer direction="column" gap="16px">
        <Block>
          <H4 align="center">{t('shipmentCreate.title')}</H4>
        </Block>

        <Block>
          <InputText
            id={XDocCreateShipmentInputId.vehicleNumber}
            label={t('shipmentCreate.vehicleNumberLabel')}
            value={shipmentState.vehicleNumber}
            onChange={(val) => handleChangeShipmentState(XDocCreateShipmentInputId.vehicleNumber, val)}
          />
        </Block>

        <InputCheckbox
          id="create-shipment-is-return"
          label={t('isReturn')}
          checked={shipmentState.isReturn}
          disabled={isPending}
          onChange={handleChangeIsReturn}
        />

        <InputSelect
          id={XDocCreateShipmentInputId.nextDropOffPointId}
          variant="primary"
          placeholder={t('shipmentCreate.dropOffPointLabel')}
          items={dropOffPoints ?? []}
          value={selectedDropOffPointLabel}
          valueId={shipmentState.nextDropOffPointId}
          disabled={isPending}
          isLoading={isDropOffPointsLoading}
          systemLanguage={i18n.language as LanguageTypes}
          error={isDropOffPointsError ? t('shipmentCreate.dropOffPointLoadError') : ''}
          onChange={(_, __, ___, id) => handleChangeShipmentState(XDocCreateShipmentInputId.nextDropOffPointId, id)}
        />

        <Button
          disabled={!shipmentState.vehicleNumber || !shipmentState.nextDropOffPointId || isPending}
          loading={isPending}
          onClick={handleSubmit}
        >
          {t('shipmentCreate.createButton')}
        </Button>
      </FlexContainer>
    </Modal>
  );
}
